import React, { useEffect } from "react";
import { NextApiResponse, NextPage } from "next";
import { useSelector } from "react-redux";
import { htmlToText } from "html-to-text";
import dynamic from "next/dynamic";
import { IEventModule } from "@Modules/eventPage/root/IEventModule";
import { HTMLHeader, LoadingSpinner } from "@Modules/common";
import { triggerPageViewAnalyticsEvent } from "repoV2/features/Common/Analytics/Analytics.utils";
import { isBrowser } from "repoV2/utils/common/render/window";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    createAction,
    uuidRegex,
    useFetchStatus,
    useCanRender,
    FETCH_STATUS,
    useEventFetched,
    setCssVar,
    QUERY_PARAMS,
    useData,
    SELECTORS,
    useEventDataOfSelectedEvent,
    ApiCall,
    API_ACTIONS,
} from "@Utils";
import { getExternalUrl, getURLSearchParamsObj } from "@Utils/urls";

import { IEventPage, IHost } from "@Interfaces";

import { logError } from "repoV2/utils/common/error/error";
import { DISABLE_CACHE_IDS } from "repoV2/constants/miscellaneous";
import { LISTING_TYPES } from "repoV2/constants/listing";
import { useRouter } from "next/router";
import { PAYMENT_PAGES_TYPES } from "repoV2/features/Payments/Payments.constants";
import { URL_SEARCH_PARAMS as THANKYOU_URL_SEARCH_PARAMS } from "@Modules/thankyou/constants/thankyou.contants";
import { URL_SEARCH_PARAMS } from "repoV2/constants/urls&routing/urlParams";
import {
    appendURLSearchParams,
    deleteURLSearchParams,
} from "repoV2/utils/urls&routing/urlParams";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { HTTP_STATUS_REDIRECT_CODES } from "repoV2/constants/apis";
import { callGetPlanPageSlugDataApi } from "@Modules/eventPage/components/PlanDropdown/utils/planSlug";
import { useFeatureAccessibility } from "repoV2/features/CreatorDetails/modules/FeatureAccessibility/utils/useFeatureAccessibility";
import useAnalyticsConfig from "repoV2/features/Common/Analytics/hooks/useAnalyticsConfig";
import { WHITELABEL_FEATURE_KEY } from "repoV2/features/CreatorPages/constants/CreatorPages.constants";
import styles from "./event.module.scss";

const EventModule = dynamic(
    () => import("@Modules/eventPage/root/OldEventPageTemplateContextProvider")
);

const EventPage: NextPage<IEventPage.IProps, IEventPage.InitialProps> = ({
    eventId,
    status,
    hostName,
    transactionId,
    initBooking, // Whether to initiate booking on open and hide the event
    initContact, // Whether to open the UserInfo modal with isPayment == false
    directBookingDiscount, //  Whether to show the discount box for direct booking
    urlQueryParams,
    planSeoPreviewData,
}) => {
    const { eventData, selectedEventUuid } = useEventDataOfSelectedEvent();
    const event = useSelector(SELECTORS.event);
    const host = useSelector(SELECTORS.host);
    const router = useRouter();
    const { query = {} } = router || {};

    const hostData = host?.dataHost?.profile_data;

    const fetchStatus = useFetchStatus(API_ACTION_TYPES.FETCH_EVENT);

    const { isFetched } = useEventFetched({ eventId });
    const { config } = useAnalyticsConfig({ hostName });

    const canRender = useCanRender(isFetched);

    const customSeoEventId = selectedEventUuid || eventId;

    const isWhitelabelActive = useFeatureAccessibility({
        featureKey: WHITELABEL_FEATURE_KEY,
    });

    const headerProps = {
        isEvent: true,
        title: eventData?.title || hostData?.name || "",
        favicon: hostData?.social_info?.favicon || hostData?.profile_pic,
        description:
            !isWhitelabelActive && hostData?.title
                ? `Hosted on Exly by ${hostData?.title}. Click on link for booking and other information`
                : (eventData?.description &&
                      htmlToText(eventData?.description)) ||
                  eventData?.title ||
                  hostData?.name,
        image: eventData?.cover_image,
        video: eventData?.cover_video,
        metaScript: hostData?.custom_meta_script,
        scriptTags: hostData?.custom_script_tags,
        customGaToken: config?.custom_ga_token,
        overrideDefaultGAToken: true,
        // Custom Seo data(title and description)
        customSeoParams:
            planSeoPreviewData || event?.list[customSeoEventId]?.seo_preview,
    };

    // The body needs to be flex for the page to not overflow
    useEffect(() => {
        triggerPageViewAnalyticsEvent({
            hostName,
            eventMeta: eventId,
        });

        const body = document.getElementsByTagName("body")[0];
        body.classList.add(styles.body);

        // The code below is an unoptimised way to keep the bottom bar from not being hidden
        // in ios instagram. This could be made better, but do remember to have it tested
        // thoroughly on ios/android instagram's in-app browser, safari, and chrome.

        const setHeightFn = (): void => {
            // Only apply this fix on instagram since it tends to drag the payment bar upwards in mobile browsers
            const isInstagram: boolean = (
                navigator?.userAgent ||
                navigator?.vendor ||
                ""
            )
                .toLocaleLowerCase()
                .includes("instagram");

            if (isInstagram) {
                const height: number = window?.innerHeight;
                setCssVar("--event-page-height", `${height}px`);
            } else {
                setCssVar("--event-page-height", `100vh`);
            }
        };
        window.addEventListener("load", setHeightFn);
        window.addEventListener("touchstart", setHeightFn);
        window.addEventListener("resize", setHeightFn);
        return () => {
            window.removeEventListener("load", setHeightFn);
            window.removeEventListener("touchstart", setHeightFn);
            window.removeEventListener("resize", setHeightFn);
        };
    }, []);

    const { data: addToCartLoading, setData: setAddToCartLoading } = useData(
        "addToCartLoading",
        false
    );

    // TODO: Resolve this issue in router. Currently it is being
    // kind-of handled in the validSubDomain middleware
    if (eventId === "favicon.ico") return null;

    const eventModuleProps: IEventModule.IProps = {
        // Basic props [Mandatory]
        eventId: selectedEventUuid || eventId,
        hostName,

        // Event data fetching related props

        // The params coming from getInitialProps are only present on the initial render.
        // The ones involving post-payment are removed once the user closes the modal. But
        // in order to update those values in the props passed down, this below-mentioned
        // conditional is being used for those values. A better solution would have a mechanism
        // to have these computed like this for all props, but that is a *TODO* for another day.

        // Post-Payment related props
        status: isBrowser() ? (query.status as string) : status,
        transactionId: isBrowser()
            ? (query.transaction as string)
            : transactionId,

        // Props for custom actions on page load
        initBooking,
        initContact,
        directBookingDiscount,
        urlQueryParams,
        addToCartCallback: () => {
            setAddToCartLoading(true);
            setTimeout(() => {
                setAddToCartLoading(false);
            }, 7000);
        },
        communityId: getURLSearchParamsObj().get(
            URL_SEARCH_PARAMS.COMMUNITY_ID
        ),
    };

    /**
     * duplication of rendered components causes apis to be hit twice
     *
     * reason for duplication of rendered components:
     * 1. needed for SEO
     * 2. using same render that was shown in server side was causing issues
     *
     * some of the known issues when same render is used
     * 1. host pages not opening for US customers - https://exly.slack.com/archives/C033Y6CMBCL/p1674024184542549
     * 2. users not geting all items in class selection dropdown in mobile - https://exly.slack.com/archives/C033Y6CMBCL/p1674015040307449
     * 3. plan link is not working properly on Android it is only showing one listing - https://exly.slack.com/archives/C033Y6CMBCL/p1673969501065769
     * 4. ExlyImage loader not getting removed - https://exly.slack.com/archives/C033Y6CMBCL/p1673956053252549
     *
     * to understand why these issues happen, read this - https://www.joshwcomeau.com/react/the-perils-of-rehydration/
     */

    if (!canRender && addToCartLoading) {
        /* The loader condition should not be couples with the rendering of the 
            EventModule above in the code below, since the EventModule needs to be 
            loader for background processing to run, even if the loader is to be shown. 
            Doing so has been known to cause critical issues, so the loader should be 
            handled independently of the render of of EventModule */
        return (
            <div className={styles.overlay}>
                <LoadingSpinner className={styles.spinner} />
            </div>
        );
    }

    // TODO: Create a PlaceHolder if not been fetched

    // PS: that SSR is still happening even with this condition.
    // You can verify this by adding console logs in the children components
    // and see if they print on the local server(terminal) or only in browser console
    if (fetchStatus !== FETCH_STATUS.FETCHED) return null;

    return (
        <>
            <section
                className={styles.eventPageContainer}
                // @dev: This id is added to disable the cacheing for Live Webinar sku
                // for more visit: repoV2/constants/miscellaneous.ts and server/cacheHandler.ts
                id={
                    eventData?.type === LISTING_TYPES.WEBINAR
                        ? DISABLE_CACHE_IDS.webinar
                        : undefined
                }
            >
                <HTMLHeader {...headerProps} />
                <EventModule {...eventModuleProps} />
            </section>
        </>
    );
};

EventPage.getInitialProps = async (
    ctx: IEventPage.IPageContext
): Promise<IEventPage.InitialProps> => {
    const { hostName } = ctx.params;
    const {
        status,
        transaction: transactionId,
        init_booking: initBooking,
        init_contact: initContact,
        enable_discount: directBookingDiscount,
        theme,
        font,
        template,
        currency,
        planSlug,
    }: IEventPage.IRouterQuery = ctx.query;

    let { eventId }: { eventId?: any } = ctx.query;

    // TODO: move above query params to `urlQueryParams`
    const dynamicLink = ctx.query[QUERY_PARAMS.DYNAMIC_LINK];
    let planId = ctx.query[QUERY_PARAMS.PLAN_ID];
    let planSeoPreviewData: IHost.IPlanSeoData | null = null;
    const community = ctx.query[URL_SEARCH_PARAMS.COMMUNITY_ID];
    const thankyouPgId = ctx.query[THANKYOU_URL_SEARCH_PARAMS.THANKYOU_PAGE_ID];
    const planSelectedEventId =
        ctx.query[URL_SEARCH_PARAMS.PLAN_SELECTED_LISTED_ID];

    // @dev
    // if old plan url of format /:eventId?planId={plan_uuid}
    // redirect to new plan url /plan/:planSlug
    // here planSlug is planId or planSlug
    // This is done to keep single url of plan and
    // migrate old urls to the new url format
    if (eventId && planId) {
        let newPath = constructRoute({
            appRouteKey: APP_ROUTE_KEYS.PLAN,
            args: { planSlug: planId as string },
        });
        // appending rest search params
        newPath = appendURLSearchParams(
            newPath,
            ctx.query as Record<string, string>
        );
        // deleting ?plan_id={value} & /:eventId search queries
        newPath = deleteURLSearchParams(newPath, [
            URL_SEARCH_PARAMS.EVENT_ID,
            URL_SEARCH_PARAMS.PLAN_ID,
        ]);
        // This line performs temporary redirect with status code 307 from serverside
        ctx.res?.writeHead(
            HTTP_STATUS_REDIRECT_CODES.HTTP_STATUS_TEMPORARY_REDIRECT,
            {
                location: newPath,
            }
        );
        ctx.res?.end();
    }

    // @dev
    // Extracting seo data for html header and saving plan details in redux
    // Plan details will be fetched and stored only in this case where
    // url is /plan/:planSlug as for /:eventId?plan_id={plan_uuid} it will be
    // redirected to former url.
    if (planSlug && !eventId && !planId) {
        try {
            // api call for plan slug details and plan details
            const data = await callGetPlanPageSlugDataApi(planSlug, hostName);
            const { plan_seo_data, plan_event_id, plan_uid, planData } = data;
            eventId = planSelectedEventId || plan_event_id;
            planId = plan_uid;
            planSeoPreviewData = plan_seo_data;
            ctx.store.dispatch(
                createAction(ACTION_TYPES.HOST.UPDATE_PLAN_DETAILS, {
                    payload: {
                        response: {
                            data: planData,
                        },
                    },
                })
            );
        } catch (error) {
            logError({
                error,
                occuredAt: "event.getInitialProps",
                when: "calling callGetPlanPageSlugDataApi",
            });
        }
    }

    const urlQueryParams = {
        [QUERY_PARAMS.ENABLE_PAYPAL]: ctx.query[QUERY_PARAMS.ENABLE_PAYPAL],
        [QUERY_PARAMS.RENEW_BOOKING]: ctx.query[QUERY_PARAMS.RENEW_BOOKING],
        [QUERY_PARAMS.COMPACT]: ctx.query[QUERY_PARAMS.COMPACT],
        [QUERY_PARAMS.DYNAMIC_LINK]: dynamicLink,
        [QUERY_PARAMS.PLAN_ID]: planId,

        // The below two keys are for continue payment
        [QUERY_PARAMS.ORDER]: ctx.query[QUERY_PARAMS.ORDER],
        [QUERY_PARAMS.ACTION]: ctx.query[QUERY_PARAMS.ACTION],
        [URL_SEARCH_PARAMS.COMMUNITY_ID]:
            ctx.query[URL_SEARCH_PARAMS.COMMUNITY_ID],
        [URL_SEARCH_PARAMS.SLUG]: ctx.query[URL_SEARCH_PARAMS.SLUG],
        [THANKYOU_URL_SEARCH_PARAMS.THANKYOU_PAGE_ID]:
            ctx.query[THANKYOU_URL_SEARCH_PARAMS.THANKYOU_PAGE_ID],
    };

    // These dispatches are not async thunks but synchronous redux
    //  actions, so no need to launch them all in parallel
    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_EVENT,
            urlArgs: {
                eventId,
                hostName, // To enable checking if the listing belongs to this host
                dynamicLink,
                paymentPageType: PAYMENT_PAGES_TYPES.CUSTOM,
                ...(transactionId && status && { currency }),
                renewSubscription:
                    ctx.query[QUERY_PARAMS.RENEW_BOOKING] === "true",
                ...(community !== undefined && { community }),
                ...(thankyouPgId && { thankyouPgId }),
            },
            errorCallback: ({ response }) => {
                // If hostName is invalid, then the redirect will be handled by the
                // utils/routing.ts > validSubDomain method that is called for all pages.
                // Calling res.redirect here for that case will cause the redirect headers
                // to be sent to the client twice, which is a no-no, so to avoid that, we
                // check if the hostName is valid (implying a redirect header hasn't already
                // been sent) before calling res.redirect here.
                if (hostName) {
                    if (response?.status === 101) {
                        // Community listing is not part of community
                        // open url without community param
                        (ctx.res as NextApiResponse).redirect(
                            deleteURLSearchParams(ctx.asPath || "", [
                                URL_SEARCH_PARAMS.COMMUNITY_ID,
                            ])
                        );
                    } else {
                        (ctx.res as NextApiResponse).redirect(
                            response?.data?.redirect_url ||
                                getExternalUrl(
                                    process.env.NEXT_PUBLIC_PROJECT_DOMAIN
                                ) ||
                                "https://exlyapp.com"
                        );
                    }
                }
            },
        })
    );

    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_HOST,
            urlArgs: {
                hostName,
                minify: true,
                utm_affiliate: ctx.query.utm_affiliate,
            },
        })
    );

    ctx.store.dispatch(
        // tags: #test
        // false && (theme || template || font)
        theme || template || font
            ? createAction(ACTION_TYPES.UTILS.API_CALL, {
                  apiActionType: API_ACTION_TYPES.FETCH_UI_CUSTOMIZATION,
                  urlArgs: {
                      themeName: theme,
                      fontName: font,
                      templateName: template,
                  },
              })
            : createAction(ACTION_TYPES.UTILS.API_CALL, {
                  apiActionType: API_ACTION_TYPES.FETCH_HOST_THEME,
                  urlArgs: { hostName },
              })
    );

    const getOfferingUrlSlugDetailsApiInfo =
        API_ACTIONS[API_ACTION_TYPES.GET_OFFERING_URL_SLUG_DETAILS];

    const isValidEventUuid: boolean = !!(
        eventId &&
        eventId.length === 36 &&
        uuidRegex.test(eventId)
    );
    let eventUuid = eventId;

    if (!isValidEventUuid) {
        try {
            const getOfferingUrlSlugDetailsApiResponse: any = await ApiCall({
                url: getOfferingUrlSlugDetailsApiInfo.url({
                    hostName,
                    slug: eventId,
                }),
            });
            eventUuid =
                getOfferingUrlSlugDetailsApiResponse?.data?.data?.listing_uuid;
        } catch (e) {
            logError({
                error: e,
                occuredAt: "getInitialProps in pages/evebt/index",
                when: "calling GET_OFFERING_URL_SLUG_DETAILS",
            });
        }
    }

    ctx.store.dispatch(
        createAction(ACTION_TYPES.UTILS.API_CALL, {
            apiActionType: API_ACTION_TYPES.FETCH_LISTING_MISCELLANEOUS_DATA,
            urlArgs: { listingUuid: eventUuid },
        })
    );

    return {
        eventId: eventId as string,
        status,
        hostName,
        transactionId,
        initBooking,
        initContact,
        directBookingDiscount,
        urlQueryParams,
        planSeoPreviewData,
    };
};

export default EventPage;
